import _orderBy from "lodash/orderBy";
import _isEmpty from "lodash/isEmpty";
import _cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { getRandomColor } from "./utils";
import { COLOR_MAPPING } from "@/constants/config";
import { getColorPalette } from "@/services/visualization/utils";
import _ from "lodash";

let primaryColor = document.querySelector(":root").style.getPropertyValue("--study-primary-color");
primaryColor = primaryColor || "#00C3B3";

/* ===============================      Exported Function        =============================== */

/**
 * Generated sites.
 * @param {Object} rowData - List countries object from API
 * @param {Boolean} hasSpecificCountry - If specific country is true, return sites under country.
 * If false, iterate object country and return all sites
 */
function generateSites(rowData, chosenCountry = "all") {
  /* IMPORTANT! */
  // Always handle error first
  if (_isEmpty(rowData) || typeof rowData !== "object") {
    return;
  }

  // Begin logic
  let tmpSites = new Set();

  if (chosenCountry === "all") {
    // Convert object countries to entries...
    const countries = Object.entries(rowData);

    // Iterating countries
    for (const sites of countries) {
      for (const site in sites[1]) {
        tmpSites.add(site);
      }
    }
  } else {
    const selectedCountry = rowData[chosenCountry];
    for (const sites in selectedCountry) {
      tmpSites.add(sites);
    }
  }

  // Sort array of object alphabetically
  tmpSites = [...tmpSites].sort((a, b) => b - a);
  return _.orderBy(Array.from(tmpSites), [site => site.toLowerCase()]);
}

/**
 * Generated countries.
 * @param {Object} rowData - List countries object from API
 */
function generateCountries(rowData) {
  /* IMPORTANT! */
  // Always handle error first
  if (_isEmpty(rowData) || typeof rowData !== "object") {
    return;
  }

  let tmpCountries = new Set();
  for (const item in rowData) {
    tmpCountries.add(item);
  }

  tmpCountries = [...tmpCountries].sort((a, b) => b - a);
  return tmpCountries;
}

const generateMapParticipantSeries = rawData => {
  if (!rawData) {
    return null;
  }
  let series = [];
  Object.entries(rawData).forEach(item => {
    series = [
      ...series,
      {
        siteName: item[0],
        country: item[1].siteDetails.siteCountry != undefined ? item[1].siteDetails.siteCountry : "Unknown",
        city: item[1].siteDetails.siteCity,
        address: item[1].siteDetails.siteAddress,
        state: item[1].siteDetails.siteState,
        postalCode: item[1].siteDetails.postalCode,
        active: item[1].siteStatus.ACTIVE != undefined ? item[1].siteStatus.ACTIVE : 0,
        activewithmodifications: item[1].siteStatus.ACTIVEWMODIFICATION != undefined ? item[1].siteStatus.ACTIVEWMODIFICATION : 0,
        disqualified: item[1].siteStatus.DISQUALIFIED != undefined ? item[1].siteStatus.DISQUALIFIED : 0,
        withdrawn: item[1].siteStatus.WITHDRAWSTUDY != undefined ? item[1].siteStatus.WITHDRAWSTUDY : 0,
        discontinued: item[1].siteStatus.DISCONTINUED != undefined ? item[1].siteStatus.DISCONTINUED : 0
      }
    ];
  });
  return series;
};

/**
 * Generated data for map visualization
 * @param {Object} boundaries - List of boundaries
 * @param {string} chosenCountries - Selected country
 * @param {Object[]} chosenSites  - Array of sites
 */
function getDataForCircleMarker(locations, chosenCountriesOld = [], chosenSites = []) {
  let chosenCountries = chosenCountriesOld;
  /* IMPORTANT! */
  // Always handle error first
  if (_isEmpty(locations) || typeof locations !== "object") {
    return;
  }

  const result = {};
  if (chosenCountries === "all") {
    chosenCountries = Object.keys(locations);
  }
  chosenCountries.forEach(countryName => {
    if (countryName in locations) {
      const countryData = locations[countryName];

      chosenSites.forEach(siteName => {
        if (siteName in countryData) {
          const cloneSiteData = _cloneDeep(countryData[siteName]);
          Object.keys(cloneSiteData.siteStatus).forEach(status => {
            if (!["ACTIVE", "ACTIVEWMODIFICATION", "DISQUALIFIED", "WITHDRAWSTUDY", "DISCONTINUED"].includes(status)) {
              delete cloneSiteData.siteStatus[status]; // remove unwanted status for maps chart. Only 4 status are allowed
            }
          });
          if (Object.keys(cloneSiteData.siteStatus).length > 0) {
            result[siteName] = cloneSiteData;
          }
        }
      });
    }
  });

  return result;
}

/**
 * Generated data for pie chart percentage of participant by status.
 * @param {Object} rawData - List countries object from API
 * @param {string} chosenCountries - Selected country
 * @param {Object[]} chosenSites  - Array of sites
 */
const generateParticipantSeries = (rawData, chosenCountries, chosenSites) => {
  if (!rawData) {
    return null;
  }
  const preSeries = {};
  let series = [];

  const convertStatus = status => {
    if (status == "SCREENFAILED") {
      return "Screen Failed";
    } else if (status == "SCREENPASSED") {
      return "Screen Passed";
    } else if (status == "ACTIVEWMODIFICATION") {
      return "Active with Modifications";
    } else if (status == "WITHDRAWSTUDY") {
      return "Withdrawn";
    } else {
      return `${status.substr(0, 1)}${status.substr(1).toLowerCase()}`;
    }
  };

  const sortStatus = ["Active", "Active with Modifications", "Verified", "Registered", "Invited", "Imported", "Screen Passed", "Prescreened", "Screen Failed", "Disqualified", "Withdrawn"];

  Object.entries(rawData).forEach(([countryName, sitesPerCountry]) => {
    (chosenCountries === "all" || chosenCountries.includes("all") || chosenCountries.includes(countryName)) &&
      Object.entries(sitesPerCountry).forEach(([siteName, site]) => {
        (chosenSites === "all" || chosenSites.includes("all") || chosenSites.includes(siteName)) &&
          Object.entries(site.siteStatus).forEach(([activityName, activityData]) => {
            if (preSeries[activityName]) {
              preSeries[activityName] += activityData;
            } else {
              preSeries[activityName] = activityData;
            }
          });
      });
  });
  const primaryColor = document.querySelector(":root").style.getPropertyValue("--study-primary-color");
  Object.entries(preSeries).forEach(([statusName]) => {
    if (statusName !== "NOTINVITED" && statusName !== "STARTED") {
      series = [
        ...series,
        {
          label: convertStatus(statusName),
          color: statusName === "ACTIVE" ? primaryColor : COLOR_MAPPING[statusName] || "#fdbf6f",
          labelBgColor: statusName === "ACTIVE" ? primaryColor : COLOR_MAPPING[status],
          // value: statusData
          value: preSeries[statusName]
        }
      ];
    }
  });
  series.sort((a, b) => sortStatus.indexOf(a.label) - sortStatus.indexOf(b.label));

  series.forEach(element => {
    if (element.label == "Activewithoutapp") {
      element.label = "Active Without App";
    }
  });

  return series;
};

/**
 * Generated data for Top 5 Sites with Most Active Participants.
 * @param {Object} rawData - List countries object from API
 * @param {string} chosenCountries - Selected country
 * @param {Object[]} chosenSites  - Array of sites
 */
const generateTop5SitesMostActiveParticipants = (rawData, chosenCountries, chosenSites) => {
  if (!rawData) {
    return null;
  }
  const preTopFive = {};
  let topFive = [];
  Object.entries(rawData).forEach(([countryName, sitesPerCountry]) => {
    (chosenCountries === "all" || chosenCountries.includes("all") || chosenCountries.includes(countryName)) &&
      Object.entries(sitesPerCountry).forEach(([siteName, site]) => {
        if (chosenSites === "all" || chosenSites.includes("all") || chosenSites.includes(siteName)) {
          if (site.siteStatus && site.siteStatus.ACTIVE) {
            if (preTopFive[siteName]) {
              preTopFive[siteName] += site.siteStatus.ACTIVE;
            } else {
              preTopFive[siteName] = site.siteStatus.ACTIVE;
            }
          }
        }
      });
  });
  Object.entries(preTopFive).forEach(([statusName]) => {
    topFive = [
      ...topFive,
      {
        siteName: statusName,
        participant: preTopFive[statusName]
      }
    ];
  });
  return _orderBy(topFive, ["participant", "value"], ["desc", "desc"]).slice(0, 5);
};

function convertMonthToTimestamps(month) {
  if (!month || typeof month !== "string") {
    return;
  }
  return new Date(moment.utc(month)).getTime();
}

function generateDataVisualizationForChart(baseWeekLables = [], dataObject = [], choosenMonth = []) {
  const returnData = [];
  const weekLabelKeys = dataObject.map(dbj => dbj.x);

  const firstMonth = typeof choosenMonth[0] === "number" ? choosenMonth[0] : convertMonthToTimestamps(choosenMonth[0]);
  const lastMonth = typeof choosenMonth[1] === "number" ? choosenMonth[1] : convertMonthToTimestamps(choosenMonth[1]);

  for (let i = 0; i < baseWeekLables.length; i++) {
    const x = baseWeekLables[i];
    const thisMonth = convertMonthToTimestamps(x);

    if (thisMonth >= firstMonth && thisMonth <= lastMonth) {
      if (weekLabelKeys.includes(x)) {
        const indexDataObject = dataObject.findIndex(ido => ido.x === x);

        const y = dataObject[indexDataObject].y > 0 ? dataObject[indexDataObject].y : 0;
        const rd = dataObject[indexDataObject].rd;

        returnData.push({ x, y, rd });
      } else {
        const y = 0;
        returnData.push({ x, y });
      }
    }
  }

  return returnData;
}

function getTotalParticipants(dataVisualization) {
  if (_.isEmpty(dataVisualization)) {
    return;
  }
  let totalParticpants = 0;
  dataVisualization.forEach(dataSet => {
    if (dataSet.filterType !== "all") {
      dataSet.data.forEach(axisData => {
        totalParticpants += Math.round(axisData.y);
      });
    }
  });
  return totalParticpants;
}

const PARAMETTER_AVEREGE_VISUALIZATIONS = "average";

function generateStudyEnrollmentProgress(rawData, chosenCountries = [], chosenSites = [], choosenMonth = []) {
  if (!rawData) {
    return null;
  }
  const { avgUnitTimeByMonthInCountries, avgUnitTimeByMonthInStudies, locations } = rawData;

  let dataVisualization = [];
  let setAverageLines = [];
  let numParticipantSites = null;
  const allAverage = [];
  const allTotal = [];

  const firstMonth = convertMonthToTimestamps(typeof choosenMonth[0] === "string" ? choosenMonth[0] : choosenMonth[0].value);
  const lastMonth = convertMonthToTimestamps(typeof choosenMonth[0] === "string" ? choosenMonth[1] : choosenMonth[1].value);

  const cloneForTotal = _.cloneDeep(avgUnitTimeByMonthInStudies);
  const initializeWeeks = cloneForTotal.map(iw => iw.x);
  const totalUnitTimeByMonthInStudies = cloneForTotal.map(iw => ({
    ...iw,
    y: iw.y2
  }));

  /* ============================================ */
  // Get All Average data base on month selection //
  /* ============================================ */

  for (const item of avgUnitTimeByMonthInStudies) {
    const averageByMonth = {};
    const thisMonth = convertMonthToTimestamps(item.x);

    if (thisMonth >= firstMonth && thisMonth <= lastMonth) {
      for (const key in item) {
        averageByMonth[key] = item[key];
      }
      allAverage.push(averageByMonth);
    }
  }

  for (const item of totalUnitTimeByMonthInStudies) {
    const totalByMonth = {};
    const thisMonth = convertMonthToTimestamps(item.x);
    if (thisMonth >= firstMonth && thisMonth <= lastMonth) {
      for (const key in item) {
        totalByMonth[key] = item[key];
      }
      allTotal.push(totalByMonth);
    }
  }

  // Generate all averages!
  const monthlyAverageArray = {
    name: `Monthly Average`,
    data: allAverage,
    backgroundColor: "rgba(54,73,93,0)",
    borderColor: primaryColor,
    borderWidth: 6,
    lineTension: 0,
    filterType: "all"
  };

  dataVisualization.push({
    name: `Monthly Total`,
    data: allTotal,
    backgroundColor: "rgba(54,73,93,0)",
    borderColor: "#00C3B3",
    borderWidth: 6,
    borderDash: 1,
    lineTension: 0,
    filterType: "total"
  });

  // below is logic for filter Country and site
  for (const countryName of chosenCountries) {
    setAverageLines = avgUnitTimeByMonthInCountries[countryName];
    const numAverage = generateDataVisualizationForChart(initializeWeeks, setAverageLines, [firstMonth, lastMonth], PARAMETTER_AVEREGE_VISUALIZATIONS);
    dataVisualization.push({
      name: `${countryName === "all" ? "study" : countryName}`,
      data: numAverage,
      backgroundColor: "rgba(54,73,93,0)",
      borderColor: getRandomColor(),
      borderWidth: 6,
      lineTension: 0,
      filterType: "country"
    });

    for (const siteName of chosenSites) {
      const siteData = locations[countryName][siteName];
      numParticipantSites = generateDataVisualizationForChart(initializeWeeks, siteData, [firstMonth, lastMonth]);
      dataVisualization.push({
        name: `${siteName}`,
        data: numParticipantSites,
        backgroundColor: "rgba(54,73,93,0)",
        borderColor: getRandomColor(),
        borderWidth: 1,
        lineTension: 0,
        filterType: "site"
      });
    }
  }

  dataVisualization = _.orderBy(dataVisualization, [dataForVisualization => dataForVisualization.name.toLowerCase()]);
  dataVisualization = [monthlyAverageArray].concat(dataVisualization);

  return dataVisualization;
}

function generateParticipantByStatus(rawData) {
  let result = null;
  if (!rawData) {
    result;
  }

  result = {};
  result["ACTIVE"] = [];
  result["DISQUALIFIED"] = [];
  result["ACTIVEWMODIFICATION"] = [];
  result["WITHDRAWSTUDY"] = [];

  const siteKey = Object.keys(rawData);
  for (let i = 0; i < siteKey.length; i++) {
    const siteName = siteKey[i];
    const siteData = rawData[siteName];
    const details = siteData ? siteData["siteDetails"] : null;
    const status = siteData ? siteData["siteStatus"] : null;

    if (status) {
      if ("ACTIVE" in status) {
        // should be ACTIVE
        result["ACTIVE"].push({
          countryName: details.siteCountry,
          siteName,
          siteDetails: details,
          // qty: status['ACTIVE'] ? Number(status['ACTIVE']) : Number(0)
          qty: Math.floor(Math.random() * 15)
        });
      }

      if ("DISQUALIFIED" in status) {
        // should be DISQUALIFIED
        result["DISQUALIFIED"].push({
          countryName: details.siteCountry,
          siteName,
          siteDetails: details,
          // qty: status['DISQUALIFIED'] ? Number(status['DISQUALIFIED']) : Number(0)
          qty: Math.floor(Math.random() * 25)
        });
      }

      if ("ACTIVEWMODIFICATION" in status) {
        // should be ACTIVEWMODIFICATION
        result["ACTIVEWMODIFICATION"].push({
          countryName: details.siteCountry,
          siteName,
          siteDetails: details,
          // qty: status['ACTIVEWMODIFICATION'] ? Number(status['ACTIVEWMODIFICATION']) : Number(0)
          qty: Math.floor(Math.random() * 35)
        });
      }

      if ("WITHDRAWSTUDY" in status) {
        // should be WITHDRAWSTUDY
        result["WITHDRAWSTUDY"].push({
          countryName: details.siteCountry,
          siteName,
          siteDetails: details,
          // qty: status['WITHDRAWSTUDY'] ? Number(status['WITHDRAWSTUDY']) : Number(0)
          qty: Math.floor(Math.random() * 15)
        });
      }
    }
  }

  return result;
}

function getBGColor(status, statusName) {
  const primaryColor = document.querySelector(":root").style.getPropertyValue("--study-primary-color");
  return statusName === "ACTIVE" ? primaryColor : COLOR_MAPPING[status];
}

function getNonComplianceRateVSRetentionRate(rawData, chosenCountries = [], chosenSites = [], tab = null) {
  const color = getColorPalette("dark").split(",");
  const response = {};
  response.country = [];
  response.sites = [];
  response.total = [
    {
      showregressionline: "1",
      anchorbgalpha: "100",
      anchorbgcolor: color[0],
      anchorBorderColor: color[0],
      data: []
    }
  ];
  let countryIndex = 0;
  let siteIndex = 0;
  response.csvData = {};
  response.csvData.total = [];
  response.csvData.country = [];
  response.csvData.sites = [];

  if (!rawData || chosenCountries.length === 0 || chosenSites.length === 0) {
    return response;
  }

  for (const [country, site] of Object.entries(rawData)) {
    if (chosenCountries.includes("all") || chosenCountries.includes(country)) {
      response.country.push({
        seriesname: country,
        showregressionline: "1",
        anchorbgalpha: "100",
        anchorbgcolor: color[countryIndex].trim(),
        anchorBorderColor: color[countryIndex].trim(),
        data: []
      });
      const sites = site;
      const siteLength = Object.keys(sites).length;
      let siteCount = 0;
      for (const siteName in sites) {
        const siteData = sites[siteName];
        if (chosenSites.includes("all") || chosenSites.includes(siteName)) {
          response.sites.push({
            seriesname: siteName,
            showregressionline: "1",
            anchorbgalpha: "100",
            anchorbgcolor: color[siteIndex].trim(),
            anchorBorderColor: color[siteIndex].trim(),
            data: []
          });
          for (const data of siteData) {
            response.total[0].data.push({
              x: Math.round(data.Non_Compliance),
              xCSV: data.Non_Compliance,
              y: Math.round(data.participantRetentionRate),
              yCSV: data.participantRetentionRate
            });
            response.country[countryIndex].data.push({
              x: Math.round(data.Non_Compliance),
              xCSV: data.Non_Compliance,
              y: Math.round(data.participantRetentionRate),
              yCSV: data.participantRetentionRate
            });
            response.sites[siteIndex].data.push({
              x: Math.round(data.Non_Compliance),
              xCSV: data.Non_Compliance,
              y: Math.round(data.participantRetentionRate),
              yCSV: data.participantRetentionRate
            });
          }
          siteData.forEach((data, index) => {
            response.csvData.total.push({
              Participant: data.participant_id,
              NonComplianceRate: response.sites[siteIndex].data[index].xCSV + "%",
              RetentionRate: response.sites[siteIndex].data[index].yCSV + "%",
              Sites: response.sites[siteIndex].seriesname,
              country: response.country[countryIndex].seriesname
            });
            response.csvData.country.push({
              country: response.country[countryIndex].seriesname,
              NonComplianceRate: response.sites[siteIndex].data[index].xCSV + "%",
              RetentionRate: response.sites[siteIndex].data[index].yCSV + "%"
            });
            response.csvData.sites.push({
              Sites: response.sites[siteIndex].seriesname,
              NonComplianceRate: response.sites[siteIndex].data[index].xCSV + "%",
              RetentionRate: response.sites[siteIndex].data[index].yCSV + "%"
            });
          });
          siteIndex += 1;
        } else if (chosenSites.includes(siteName) === false) {
          siteCount += 1;
          if (siteLength === 0 || siteCount === siteLength) {
            response.country[countryIndex].seriesname = "";
          }
        }
      }
      countryIndex += 1;
    }
  }

  const totalData = response.total.filter(obj => !(obj && obj.data.length === 0));
  const countryData = response.country.filter(obj => !(obj && obj.data.length === 0));
  const siteData = response.sites.filter(obj => !(obj && obj.data.length === 0));

  let responseData = {};
  responseData.csvData = [];

  switch (tab) {
    case "Total": {
      responseData = totalData;
      responseData.csvData = response.csvData.total;
      break;
    }
    case "Countries": {
      responseData = countryData.sort((a, b) => {
        return a.seriesname.toLowerCase() > b.seriesname.toLowerCase() ? 1 : b.seriesname.toLowerCase() > a.seriesname.toLowerCase() ? -1 : 0;
      });
      responseData.csvData = response.csvData.country.sort((a, b) => {
        return a.country.toLowerCase() > b.country.toLowerCase() ? 1 : b.country.toLowerCase() > a.country.toLowerCase() ? -1 : 0;
      });
      break;
    }
    case "Sites": {
      responseData = siteData.sort((a, b) => {
        return a.seriesname.toLowerCase() > b.seriesname.toLowerCase() ? 1 : b.seriesname.toLowerCase() > a.seriesname.toLowerCase() ? -1 : 0;
      });
      responseData.csvData = response.csvData.sites.sort((a, b) => {
        return a.Sites.toLowerCase() > b.Sites.toLowerCase() ? 1 : b.Sites.toLowerCase() > a.Sites.toLowerCase() ? -1 : 0;
      });
      break;
    }
    default:
      break;
  }

  return responseData;
}

export { generateCountries, generateSites, getDataForCircleMarker, generateParticipantSeries, generateTop5SitesMostActiveParticipants, generateStudyEnrollmentProgress, getTotalParticipants, convertMonthToTimestamps, generateMapParticipantSeries, getBGColor, generateParticipantByStatus, getNonComplianceRateVSRetentionRate };
